import React, { useMemo } from "react";
import {
  HiOutlineChevronRight,
  HiOutlineExclamation,
  HiOutlineSparkles,
} from "react-icons/hi";
import {
  buildAiStatusFromBatchItem,
  buildTimesheetRowLabel,
  collectAllChecksFromReport,
  formatReadinessPercent,
  normalizeAiReportPayload,
  resolveAiAnomalyResultState,
  resolveAiOverallUiState,
  resolveTimesheetAiErrorMessage,
  summarizeAiChecks,
  truncateAiText,
} from "../utils/timesheetAiUtils";

const formatAud = (value, digits = 4) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0.0000";
  return number.toFixed(digits);
};

const TONE_STYLES = {
  red: {
    icon: "bg-red-50 text-red-500 ring-red-100",
    badge: "bg-red-100 text-red-700",
    hint: "text-red-600",
  },
  amber: {
    icon: "bg-amber-50 text-amber-600 ring-amber-100",
    badge: "bg-amber-100 text-amber-800",
    hint: "text-amber-700",
  },
  emerald: {
    icon: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    badge: "bg-emerald-100 text-emerald-800",
    hint: "text-emerald-700",
  },
  gray: {
    icon: "bg-gray-100 text-gray-500 ring-gray-200",
    badge: "bg-gray-100 text-gray-700",
    hint: "text-gray-600",
  },
};

const CheckStatsBar = ({ summary }) => {
  if (!summary || summary.total <= 0) return null;

  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      <span className='inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-inset ring-gray-100'>
        <span className='text-emerald-700'>{summary.pass}</span>
        <span className='mx-0.5 text-gray-400'>/</span>
        <span>{summary.total}</span>
        <span className='ml-1 text-gray-500'>passed</span>
      </span>
      {summary.critical > 0 ? (
        <span className='inline-flex rounded-md bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-inset ring-red-100'>
          {summary.critical} critical
        </span>
      ) : null}
      {summary.review > 0 ? (
        <span className='inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-100'>
          {summary.review} to fix
        </span>
      ) : null}
    </div>
  );
};

const ResultRow = ({ item, rowMeta, onViewReport }) => {
  const status = buildAiStatusFromBatchItem(item);
  const { title, subtitle } = buildTimesheetRowLabel(rowMeta);
  const timesheetId = Number(item?.timesheet_id);
  const data = item?.data || {};
  const normalized = item?.success ? normalizeAiReportPayload(data) : null;
  const checkSummary = normalized
    ? summarizeAiChecks(collectAllChecksFromReport(normalized))
    : status?.checkSummary;
  const allChecks = normalized
    ? collectAllChecksFromReport(normalized)
    : [];
  const resultState = item?.success
    ? resolveAiOverallUiState({
        overall: normalized?.overall,
        checks: allChecks,
        anomaly_score: data?.anomaly_score,
      })
    : resolveAiAnomalyResultState({
        success: item?.success,
        failed: true,
        failMessage: resolveTimesheetAiErrorMessage(item),
      });
  const styles = TONE_STYLES[resultState.tone] || TONE_STYLES.gray;
  const scoreHint =
    resultState.scoreHint ||
    (normalized?.overall?.readiness_score != null
      ? `Readiness ${formatReadinessPercent(normalized.overall.readiness_score)}`
      : null);

  const handleOpen = () => {
    if (!Number.isFinite(timesheetId) || timesheetId <= 0) return;
    onViewReport?.(timesheetId, status);
  };

  return (
    <button
      type='button'
      onClick={handleOpen}
      aria-label={`View AI report for ${title}`}
      className='group flex w-full cursor-pointer gap-3 px-4 py-3 text-left transition-colors hover:bg-[#ECF0FD]/60'
    >
      <span
        className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ${styles.icon}`}
      >
        {resultState.key === "failed" ? (
          <HiOutlineExclamation className='h-4 w-4' />
        ) : resultState.key === "critical" ? (
          <HiOutlineExclamation className='h-4 w-4' />
        ) : (
          <HiOutlineSparkles className='h-4 w-4' />
        )}
      </span>

      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-3'>
          <div className='min-w-0 flex-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <p className='text-sm font-semibold text-gray-900'>{title}</p>
              <span
                className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${styles.badge}`}
              >
                {resultState.shortLabel}
              </span>
            </div>
            {subtitle ? (
              <p className='mt-0.5 text-xs text-gray-500'>{subtitle}</p>
            ) : null}
          </div>

          <div className='flex shrink-0 items-center gap-1.5 pt-0.5'>
            {scoreHint ? (
              <span className={`text-right text-[11px] font-semibold leading-tight ${styles.hint}`}>
                {scoreHint}
              </span>
            ) : null}
            <HiOutlineChevronRight className='h-4 w-4 shrink-0 text-gray-300 transition group-hover:text-[#4B6CB7]' />
          </div>
        </div>

        {!item?.success ? (
          <p className='mt-2 text-sm leading-snug text-red-700'>
            {truncateAiText(resultState.description, 120)}
          </p>
        ) : null}

        {item?.success && checkSummary?.total > 0 ? (
          <div className='mt-2'>
            <CheckStatsBar summary={checkSummary} />
          </div>
        ) : null}
      </div>
    </button>
  );
};

/** Compact batch summary with per-timesheet rows — sits below the timesheet table. */
const TimesheetAiResultsPanel = ({ result, timesheets = [], onViewReport, onClose }) => {
  const summary = result?.summary || {};
  const rows = result?.results || [];

  const rowById = useMemo(() => {
    const map = {};
    (timesheets || []).forEach((row) => {
      const id = Number(row?.id);
      if (Number.isFinite(id) && id > 0) {
        map[id] = row;
      }
    });
    return map;
  }, [timesheets]);

  const stateCounts = useMemo(() => {
    let passed = 0;
    let review = 0;
    let critical = 0;
    let unavailable = 0;
    rows.forEach((item) => {
      if (!item?.success) {
        unavailable += 1;
        return;
      }
      const normalized = normalizeAiReportPayload(item?.data || {});
      const allChecks = collectAllChecksFromReport(normalized);
      const state = resolveAiOverallUiState({
        overall: normalized?.overall,
        checks: allChecks,
        anomaly_score: item?.data?.anomaly_score,
      });
      if (state.key === "passed" || state.key === "ready") passed += 1;
      else if (state.key === "critical") critical += 1;
      else review += 1;
    });
    return { passed, review, critical, unavailable };
  }, [rows]);

  if (!result || rows.length === 0) return null;

  const listMaxHeight = Math.min(120 + rows.length * 96, 360);

  return (
    <section className='mt-3 overflow-hidden rounded-xl border border-blue-200 bg-white shadow-sm'>
      <div className='flex flex-wrap items-center justify-between gap-3 border-b border-blue-100 bg-gradient-to-r from-[#ECF0FD] to-white px-4 py-3'>
        <div className='flex min-w-0 items-center gap-2.5'>
          <span className='inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white text-[#4B6CB7] shadow-sm ring-1 ring-blue-100'>
            <HiOutlineSparkles className='h-4 w-4' />
          </span>
          <div>
            <p className='text-sm font-semibold text-gray-600'>AI analysis results</p>
            <div className='mt-1 flex flex-wrap items-center gap-1.5'>
              {stateCounts.passed > 0 ? (
                <span className='inline-flex rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-100'>
                  {stateCounts.passed} passed
                </span>
              ) : null}
              {stateCounts.review > 0 ? (
                <span className='inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900 ring-1 ring-inset ring-amber-100'>
                  {stateCounts.review} review
                </span>
              ) : null}
              {stateCounts.critical > 0 ? (
                <span className='inline-flex rounded-md bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700 ring-1 ring-inset ring-red-100'>
                  {stateCounts.critical} critical
                </span>
              ) : null}
              {stateCounts.unavailable > 0 ? (
                <span className='inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700 ring-1 ring-inset ring-gray-200'>
                  {stateCounts.unavailable} unavailable
                </span>
              ) : null}
              <span className='text-[11px] text-gray-500'>
                ${formatAud(summary.total_cost_aud, 4)} AUD
              </span>
            </div>
          </div>
        </div>
        <button
          type='button'
          onClick={onClose}
          className='shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold text-[#4B6CB7] transition hover:bg-white hover:text-[#293F6A]'
        >
          Dismiss
        </button>
      </div>

      <div
        className='overflow-y-auto'
        style={{ maxHeight: listMaxHeight }}
      >
        {rows.map((item, index) => {
          const id = Number(item?.timesheet_id);
          return (
            <React.Fragment key={id || item?.message}>
              {index > 0 ? (
                <div
                  className='mx-4 border-t border-[#BFDBFE]'
                  role='separator'
                  aria-hidden='true'
                />
              ) : null}
              <ResultRow
                item={item}
                rowMeta={rowById[id]}
                onViewReport={onViewReport}
              />
            </React.Fragment>
          );
        })}
      </div>

      <p className='border-t border-gray-100 bg-gray-50/80 px-4 py-2.5 text-[11px] leading-relaxed text-gray-500'>
        Click a row to open the full AI report ·{" "}
        <span className='font-semibold text-emerald-700'>Passed</span> ·{" "}
        <span className='font-semibold text-amber-700'>Review</span> ·{" "}
        <span className='font-semibold text-red-600'>Critical</span>
      </p>
    </section>
  );
};

export default TimesheetAiResultsPanel;
